import type { Database } from 'sql.js';
import { queryAll, type SqlJsBind } from '@core/infrastructure/driven/sqljs/sqljsSqlitePrimitives.ts';
import type { LocalSqliteRuntime } from '@src/modules/shared/local-sqlite/domain/LocalSqliteRuntime.ts';
import type {
  ObservabilidadEventRow,
  ObservabilidadEventsFilter,
  ObservabilidadHttpRow,
  ObservabilidadListLimit,
  ObservabilidadLogRow,
  ObservabilidadLogsFilter,
  ObservabilidadMetricRow,
  ObservabilidadPersistedReadPort,
  ObservabilidadTraceRow,
  ObservabilidadTracesFilter,
} from '../../domain/ports/ObservabilidadPersistedReadPort.ts';

const MAX_ROWS = 500;

/** Escapes `%`, `_`, and `\` for SQLite `LIKE` with `ESCAPE '\\'`. */
export function escapeSqlLikeFragment(fragment: string): string {
  return fragment.replace(/\\/g, '\\\\').replace(/%/g, '\\%').replace(/_/g, '\\_');
}

function clampLimit(raw?: number): number {
  const n = raw == null ? 150 : Math.floor(Number(raw));
  if (!Number.isFinite(n)) return 150;
  return Math.min(MAX_ROWS, Math.max(1, n));
}

function str(v: SqlJsBind | undefined): string {
  if (v == null || v === '') return '';
  return String(v);
}

function optStr(v: SqlJsBind | undefined): string | null {
  const s = str(v);
  return s === '' ? null : s;
}

/**
 * Normalizes heterogeneous sql.js SQLite scalar binds into telemetry-friendly numbers.
 */
export function coerceSqlJsTelemetryNumericBind(v: SqlJsBind | undefined): number {
  if (v == null || v === '') return 0;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

const num = coerceSqlJsTelemetryNumericBind;

function mapLog(row: Record<string, SqlJsBind>): ObservabilidadLogRow {
  const nivel = str(row.nivel);
  return {
    id: num(row.id),
    creado_en: str(row.creado_en),
    nivel: nivel === 'error' ? 'error' : 'info',
    mensaje: str(row.mensaje),
    detalle_json: optStr(row.detalle_json),
    correlacion_id: optStr(row.correlacion_id),
    usuario_id: optStr(row.usuario_id),
  };
}

function mapEvent(row: Record<string, SqlJsBind>): ObservabilidadEventRow {
  return {
    id: num(row.id),
    creado_en: str(row.creado_en),
    nombre_evento: str(row.nombre_evento),
    payload_json: optStr(row.payload_json),
    usuario_id: optStr(row.usuario_id),
    correlacion_id: optStr(row.correlacion_id),
    url_pagina: optStr(row.url_pagina),
  };
}

function mapTrace(row: Record<string, SqlJsBind>): ObservabilidadTraceRow {
  const estado = str(row.estado);
  return {
    id: num(row.id),
    creado_en: str(row.creado_en),
    nombre_span: str(row.nombre_span),
    estado: estado === 'error' ? 'error' : 'ok',
    duracion_ms: Math.floor(num(row.duracion_ms)),
    error_mensaje: optStr(row.error_mensaje),
    usuario_id: optStr(row.usuario_id),
    correlacion_id: optStr(row.correlacion_id),
    url_pagina: optStr(row.url_pagina),
  };
}

function mapMetric(row: Record<string, SqlJsBind>): ObservabilidadMetricRow {
  const tipo = str(row.tipo);
  return {
    id: num(row.id),
    creado_en: str(row.creado_en),
    tipo: tipo === 'histogram' ? 'histogram' : 'counter',
    nombre: str(row.nombre),
    valor: num(row.valor),
    atributos_json: optStr(row.atributos_json),
    usuario_id: optStr(row.usuario_id),
  };
}

function mapHttp(row: Record<string, SqlJsBind>): ObservabilidadHttpRow {
  const tipo = str(row.tipo);
  return {
    id: num(row.id),
    creado_en: str(row.creado_en),
    tipo: tipo === 'response' ? 'response' : 'request',
    correlacion_id: str(row.correlacion_id),
    metodo: optStr(row.metodo),
    url: str(row.url),
    codigo_estado: row.codigo_estado == null ? null : Math.floor(num(row.codigo_estado)),
    duracion_ms: row.duracion_ms == null ? null : Math.floor(num(row.duracion_ms)),
    usuario_id: optStr(row.usuario_id),
  };
}

/** Reads `observabilidad_*` tables via sql.js (best-effort if tables missing). */
export class SqlJsObservabilidadPersistedRead implements ObservabilidadPersistedReadPort {
  constructor(private readonly getDb: () => Database | undefined) {}

  get available(): boolean {
    return Boolean(this.getDb());
  }

  private db(): Database | undefined {
    return this.getDb();
  }

  private safeQuery(sql: string, params: SqlJsBind[]): Record<string, SqlJsBind>[] {
    const db = this.db();
    if (!db) return [];
    try {
      return queryAll(db, sql, params);
    } catch {
      return [];
    }
  }

  listLogs(filter: ObservabilidadLogsFilter, limit: ObservabilidadListLimit): ObservabilidadLogRow[] {
    const parts: string[] = [];
    const params: SqlJsBind[] = [];
    const nivel = filter.nivel ?? 'all';
    if (nivel !== 'all') {
      parts.push('nivel = ?');
      params.push(nivel);
    }
    const qMsg = filter.mensajeContains?.trim();
    if (qMsg) {
      parts.push(`mensaje LIKE ? ESCAPE '\\'`);
      params.push(`%${escapeSqlLikeFragment(qMsg)}%`);
    }
    const corr = filter.correlacionId?.trim();
    if (corr) {
      parts.push('correlacion_id = ?');
      params.push(corr);
    }
    const where = parts.length ? `WHERE ${parts.join(' AND ')}` : '';
    const lim = clampLimit(limit.maxRows);
    const sql = `SELECT id, creado_en, nivel, mensaje, detalle_json, correlacion_id, usuario_id FROM observabilidad_logs ${where} ORDER BY id DESC LIMIT ?`;
    params.push(lim);
    return this.safeQuery(sql, params).map(mapLog);
  }

  listEvents(filter: ObservabilidadEventsFilter, limit: ObservabilidadListLimit): ObservabilidadEventRow[] {
    const parts: string[] = [];
    const params: SqlJsBind[] = [];
    if (filter.nombreIndicatesError) {
      parts.push(`LOWER(CAST(nombre_evento AS TEXT)) LIKE '%error%'`);
    } else {
      const qName = filter.nombreContains?.trim();
      if (qName) {
        parts.push(`LOWER(CAST(nombre_evento AS TEXT)) LIKE ? ESCAPE '\\'`);
        params.push(`%${escapeSqlLikeFragment(qName.toLowerCase())}%`);
      }
    }
    const corr = filter.correlacionId?.trim();
    if (corr) {
      parts.push('correlacion_id = ?');
      params.push(corr);
    }
    const where = parts.length ? `WHERE ${parts.join(' AND ')}` : '';
    const lim = clampLimit(limit.maxRows);
    const sql = `SELECT id, creado_en, nombre_evento, payload_json, usuario_id, correlacion_id, url_pagina FROM observabilidad_eventos ${where} ORDER BY id DESC LIMIT ?`;
    params.push(lim);
    return this.safeQuery(sql, params).map(mapEvent);
  }

  listTraces(filter: ObservabilidadTracesFilter, limit: ObservabilidadListLimit): ObservabilidadTraceRow[] {
    const parts: string[] = [];
    const params: SqlJsBind[] = [];
    const estado = filter.estado ?? 'all';
    if (estado !== 'all') {
      parts.push('estado = ?');
      params.push(estado);
    }
    const qSpan = filter.nombreContains?.trim();
    if (qSpan) {
      parts.push(`LOWER(CAST(nombre_span AS TEXT)) LIKE ? ESCAPE '\\'`);
      params.push(`%${escapeSqlLikeFragment(qSpan.toLowerCase())}%`);
    }
    const corr = filter.correlacionId?.trim();
    if (corr) {
      parts.push('correlacion_id = ?');
      params.push(corr);
    }
    const where = parts.length ? `WHERE ${parts.join(' AND ')}` : '';
    const lim = clampLimit(limit.maxRows);
    const sql = `SELECT id, creado_en, nombre_span, estado, duracion_ms, error_mensaje, usuario_id, correlacion_id, url_pagina FROM observabilidad_trazas ${where} ORDER BY id DESC LIMIT ?`;
    params.push(lim);
    return this.safeQuery(sql, params).map(mapTrace);
  }

  listMetrics(limit: ObservabilidadListLimit): ObservabilidadMetricRow[] {
    const lim = clampLimit(limit.maxRows);
    const sql =
      'SELECT id, creado_en, tipo, nombre, valor, atributos_json, usuario_id FROM observabilidad_metricas ORDER BY id DESC LIMIT ?';
    return this.safeQuery(sql, [lim]).map(mapMetric);
  }

  listHttp(limit: ObservabilidadListLimit): ObservabilidadHttpRow[] {
    const lim = clampLimit(limit.maxRows);
    const sql =
      'SELECT id, creado_en, tipo, correlacion_id, metodo, url, codigo_estado, duracion_ms, usuario_id FROM observabilidad_http ORDER BY id DESC LIMIT ?';
    return this.safeQuery(sql, [lim]).map(mapHttp);
  }
}

/** Factory for composition root; returns a reader even when offline DB is absent (`available === false`). */
export function createSqlJsObservabilidadPersistedRead(
  runtime: LocalSqliteRuntime | undefined,
): ObservabilidadPersistedReadPort {
  return new SqlJsObservabilidadPersistedRead(() => runtime?.getDb());
}
